<script setup lang="ts">
  import { Chip, ChipSize, Icon, IconName, IconSize } from '@travelata-libs/ui-kit';
  import { computed, nextTick, onMounted, ref } from 'vue';
  import { SERP_QUICK_FILTERS_TEXTS, SERP_SORT_LABELS } from '../config/texts';
  import type { SerpFilterGroupId, SerpQuickFilterView, SerpSort } from '../model/types';

  interface SerpQuickFiltersProps {
    sort: SerpSort;
    filtersCount?: number;
    quickFilters?: SerpQuickFilterView[];
    scrollLeft?: number;
  }

  const props = withDefaults(defineProps<SerpQuickFiltersProps>(), {
    filtersCount: 0,
    quickFilters: () => [],
    scrollLeft: 0,
  });

  interface SerpQuickFiltersEmits {
    (e: 'filters'): void;
    (e: 'sort'): void;
    (e: 'open', groupId: SerpFilterGroupId): void;
    (e: 'toggle', groupId: SerpFilterGroupId): void;
    (e: 'clear', groupId: SerpFilterGroupId): void;
    (e: 'scroll', scrollLeft: number): void;
  }

  const emit = defineEmits<SerpQuickFiltersEmits>();

  const rootRef = ref<HTMLElement | null>(null);

  const allFiltersLabel = computed<string>(() => {
    const { allFilters, selectedCount } = SERP_QUICK_FILTERS_TEXTS;

    return props.filtersCount > 0 ? `${allFilters}, ${selectedCount} ${props.filtersCount}` : allFilters;
  });

  const clearLabel = (quickFilter: SerpQuickFilterView): string =>
    `${SERP_QUICK_FILTERS_TEXTS.clear}: ${quickFilter.label}`;

  onMounted(async () => {
    await nextTick();
    if (rootRef.value) rootRef.value.scrollLeft = props.scrollLeft;
  });

  const onScroll = (): void => {
    emit('scroll', rootRef.value?.scrollLeft ?? 0);
  };

  const onQuickFilterClick = (quickFilter: SerpQuickFilterView): void => {
    if (quickFilter.control === 'checkbox') {
      emit('toggle', quickFilter.id);
      return;
    }

    if (quickFilter.available) {
      emit('open', quickFilter.id);
      return;
    }

    emit('filters');
  };
</script>

<template>
  <div
    ref="rootRef"
    class="serp-quick-filters"
    data-testid="serp-quick-filters"
    @scroll.passive="onScroll"
  >
    <Chip
      class="serp-quick-filters__item serp-quick-filters__item_all"
      rounded
      :size="ChipSize.M"
      :aria-label="allFiltersLabel"
      data-testid="serp-quick-filters-all"
      @click="$emit('filters')"
    >
      <template #prepend>
        <Icon
          class="serp-quick-filters__icon"
          :name="IconName.Filters"
          :size="IconSize.M"
        />
        <span
          v-if="filtersCount > 0"
          class="serp-quick-filters__badge"
          data-testid="serp-quick-filters-badge"
        />
      </template>
    </Chip>
    <Chip
      class="serp-quick-filters__item"
      rounded
      :size="ChipSize.M"
      :aria-label="SERP_QUICK_FILTERS_TEXTS.sort"
      data-testid="serp-quick-filters-sort"
      @click="$emit('sort')"
    >
      <template #prepend>
        <svg
          class="serp-quick-filters__sort-icon"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            d="M7 20V4m-3.5 3.5L7 4l3.5 3.5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M17 4v16m-3.5-3.5L17 20l3.5-3.5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </template>
      {{ SERP_SORT_LABELS[sort] }}
    </Chip>
    <Chip
      v-for="quickFilter in quickFilters"
      :key="quickFilter.id"
      class="serp-quick-filters__item"
      rounded
      :size="ChipSize.M"
      :active="quickFilter.selected"
      :data-testid="`serp-quick-filters-${quickFilter.id}`"
      @click="onQuickFilterClick(quickFilter)"
    >
      {{ quickFilter.label }}
      <template #append>
        <button
          v-if="quickFilter.selected"
          type="button"
          class="serp-quick-filters__clear"
          :aria-label="clearLabel(quickFilter)"
          :data-testid="`serp-quick-filters-${quickFilter.id}-clear`"
          @click.stop="emit('clear', quickFilter.id)"
        >
          <Icon
            :name="IconName.Close"
            :size="IconSize.M"
          />
        </button>
        <Icon
          v-else-if="quickFilter.control !== 'checkbox'"
          class="serp-quick-filters__chevron"
          :name="IconName.ChevronDown"
          :size="IconSize.M"
          :data-testid="`serp-quick-filters-${quickFilter.id}-chevron`"
        />
      </template>
    </Chip>
  </div>
</template>

<style scoped lang="scss">
  .serp-quick-filters {
    align-items: center;
    box-sizing: border-box;
    display: flex;
    gap: var(--spacing-xxs);
    overflow-x: auto;
    padding: var(--spacing-s) var(--spacing-m);
    scrollbar-width: none;
    width: 100%;

    &::-webkit-scrollbar {
      display: none;
    }

    &__client {
      display: contents;
    }

    &__item {
      cursor: pointer;
      flex: 0 0 auto;

      &_all {
        position: relative;

        // ui-kit рисует пустой .tl-chip__source и у чипа-иконки, он раздувает чип на 4px
        ::v-deep .tl-chip__source {
          display: none;
        }
      }
    }

    & &__item {
      gap: var(--spacing-xxs);
    }

    & &__item_all {
      padding: 0;
      width: 44px;
    }

    &__badge {
      background: var(--color-red-50);
      border-radius: 50%;
      height: var(--spacing-xs);
      left: calc(var(--spacing-s) + var(--icon-m));
      position: absolute;
      top: calc(-1 * var(--spacing-xxxs) - 1px);
      width: var(--spacing-xs);
    }

    &__icon,
    &__chevron {
      fill: var(--color-text-secondary);
    }

    &__clear {
      align-items: center;
      background: none;
      border: 0;
      color: inherit;
      cursor: pointer;
      display: inline-flex;
      fill: inherit;
      margin: calc(-1 * var(--spacing-xxs)) calc(-1 * var(--spacing-s)) calc(-1 * var(--spacing-xxs))
        calc(-1 * var(--spacing-xxs));
      padding: var(--spacing-xxs) var(--spacing-s) var(--spacing-xxs) var(--spacing-xxs);
    }

    &__sort-icon {
      color: var(--color-text-secondary);
      display: block;
      height: var(--icon-m);
      width: var(--icon-m);
    }
  }
</style>
